import { api } from '../api';
import { Link } from '../router';
import { useSession } from '../session';
import { useData } from '../useData';
import { Shell } from '../components/Shell';
import { LogTime } from '../components/LogTime';
import { Empty, hoursOf, Loading, stateColour, useStateName, when } from '../components/bits';
import { DAILY_TARGET_HOURS, lastDays } from '../workday';

/** Your own page: what is on your plate, and a way to log against it.
 *
 *  It used to be the settings form with a couple of lists under it, which read
 *  as a form you had to get past. Editing your name is a rare errand; seeing
 *  what is assigned to you and putting down this morning's hours is the daily
 *  one. So the settings moved to their own page and this became the overview
 *  Drupal's user page was, without the Drupal.
 */
export function Profile() {
  const { t, locale, user } = useSession();
  const stateName = useStateName();

  // What is under way, then what has been handed over and not started: a new
  // task assigned to you is on your plate as much as one you have begun.
  const mine = useData(async () => {
    const [started, waiting] = await Promise.all([api.myTasks('in_progress'), api.myTasks('new')]);
    return [...started, ...waiting];
  }, []);
  const recent = useData(() => api.myTime(14), []);

  if (!user) return <Shell><Loading /></Shell>;

  return (
    <Shell>
      <div className="page-head">
        <div className="grow">
          <p className="kicker">{user.jobTitle || user.email}</p>
          <h1>{user.name}</h1>
        </div>
        <Link to="/settings" className="link">{t.editProfile} &gt;</Link>
      </div>

      {user.isStaff && (
        <section style={{ marginBottom: 'var(--sp-6)' }}>
          <LogTime onLogged={recent.reload} />
        </section>
      )}

      {/* A client has no hours panel, so the two-column split would leave
          half the page empty beside their task list. */}
      <div className={user.isStaff ? 'split' : ''}>
        <section>
          <h2>{t.myTasks}</h2>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>{t.myTasksHint}</p>
          {mine.loading && !mine.data ? <Loading /> : null}
          {mine.data?.length === 0 ? <Empty>{t.noTasks}</Empty> : null}
          {mine.data && mine.data.length > 0 && (
            <table style={{ marginTop: 'var(--sp-3)' }}>
              <tbody>
                {mine.data.map((task) => (
                  <tr key={task.id}>
                    <td><Link to={`/tasks/${task.id}`}>{task.title}</Link></td>
                    <td className="muted">{task.projectTitle}</td>
                    <td style={{ color: stateColour[task.state], whiteSpace: 'nowrap' }}>{stateName(task.state)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {user.isStaff && (
          <section>
            <h2>{t.recentTime}</h2>
            {recent.loading && !recent.data ? <Loading /> : null}
            {recent.data && <Fortnight totals={recent.data} userID={user.id} locale={locale} />}
          </section>
        )}
      </div>
    </Shell>
  );
}

/** The fortnight, day by day, so somebody can see at a glance whether they put
 *  their day down.
 *
 *  Days with nothing logged are the point of the block, so they are here too:
 *  a day you forgot has no rows, and a query that groups rows cannot return
 *  it. Weekends only appear when there is something on them.
 */
function Fortnight({
  totals, userID, locale,
}: {
  totals: { day: string; minutes: number }[];
  userID: number;
  locale: string;
}) {
  const { t } = useSession();
  const days = lastDays(totals, 14);
  const total = days.reduce((sum, d) => sum + d.minutes, 0);
  const owed = days.reduce(
    (sum, d) => sum + (d.short ? DAILY_TARGET_HOURS * 60 - d.minutes : 0), 0);

  if (days.length === 0) return <Empty>{t.noTime}</Empty>;

  return (
    <table style={{ marginTop: 'var(--sp-3)' }}>
      <tbody>
        {days.map((d) => (
          <tr key={d.day} className={d.short ? 'short' : d.weekend ? 'weekend' : undefined}>
            <td className="date">
              {/* The day opens the manager already narrowed to that day and
                  this person; the filters there stay editable. */}
              <Link to={`/time?userId=${userID}&from=${d.day}&to=${d.day}`}>
                {when(d.day, locale)}
              </Link>
            </td>
            <td className="gap">
              {d.minutes === 0
                ? t.nothingLogged
                : d.short
                  ? t.shortBy(hoursOf(DAILY_TARGET_HOURS * 60 - d.minutes))
                  : ''}
            </td>
            <td className="num">{d.minutes === 0 ? '—' : `${hoursOf(d.minutes)}h`}</td>
          </tr>
        ))}
        <tr className="sum">
          <td>{t.total}</td>
          <td className="gap">{owed > 0 ? t.shortBy(hoursOf(owed)) : ''}</td>
          <td className="num">{hoursOf(total)}h</td>
        </tr>
      </tbody>
    </table>
  );
}
